import { lazy, useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { FaDownload } from "react-icons/fa";
import DesktopView from "../layout/DesktopView";
import ShowcaseCard from "./ShowcaseCard";
import PromptEditor from "../prompts/PromptEditor";
import TagDropdown from "../common/TagDropdown";
import usePins from "../../hooks/usePins";
import {
  migratePrompts,
  migratePromptData,
} from "../../services/promptService";
import {
  createTopic,
  updateTopic,
  createTopicVersion,
  getAllTopics,
  getDeletedTopics,
  importTopics,
} from "../../services/topicService";
import { useToast } from "../common/ToastContext";
import { useCardSelector } from "../common/CardSelectorProvider";
import ImportModal from "../common/ImportModal";
import { currentUserHasRole } from "../../services/userService";
import { tagService } from "../../services/tagService";
import ToggleControl from "../common/ToggleControl";
import LoadingTopicCard from "./LoadingTopicCard";
import { v4 as uuidv4 } from "uuid";

// Module scope on purpose: lazy() inside the component would mint a new component
// type on every render, remounting every card (and refetching its data) instead of
// reordering them.
const LazyTopicCard = lazy(() => import("./TopicCard"));
const LazyCompactTopicCard = lazy(() => import("./CompactTopicCard"));

const TopicDesktop = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tags, setTags] = useState([]);
  const [allTopics, setAllTopics] = useState([]); // Store all topics
  const [topics, setTopics] = useState([]); // Store filtered topics
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [goBack, setGoBack] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const [importing, setImporting] = useState(false);
  const { showToast } = useToast();
  const { openCardSelector } = useCardSelector();
  const { sortPinnedFirst } = usePins("topics");

  const getStoredView = () =>
    localStorage.getItem("currentView") || "developer";
  const getStoredCompactView = () =>
    localStorage.getItem("compactView") === "true";

  const setStoredView = (view) => localStorage.setItem("currentView", view);
  const setStoredCompactView = (isCompact) =>
    localStorage.setItem("compactView", isCompact.toString());

  const [currentView, setCurrentView] = useState(getStoredView);
  const [compactView, setCompactView] = useState(getStoredCompactView);

  const urlParams = new URLSearchParams(window.location.search);
  const hasMigrateTopicsEnabled =
    urlParams.has("migrate_topics") && currentUserHasRole("admin");
  const hasMigrateTagsEnabled =
    urlParams.has("migrate_tags") && currentUserHasRole("admin");
  const hasMigratePromptsEnabled =
    urlParams.has("migrate_prompts") && currentUserHasRole("admin");

  useEffect(() => {
    setStoredView(currentView);
  }, [currentView]);

  useEffect(() => {
    setStoredCompactView(compactView);
  }, [compactView]);

  const fetchTags = async () => {
    try {
      const tags = await tagService.getAllTags();
      if (tags) {
        const sortedTags = tags.sort((a, b) => a.name.localeCompare(b.name));
        setTags(sortedTags);
      }
    } catch (error) {
      console.error("Error fetching tags:", error);
    }
  };

  const fetchTopics = useCallback(async () => {
    try {
      const [fetchedTopics, searchKey] = await Promise.all([
        getAllTopics(),
        searchParams.get("key"),
      ]);

      const sortedTopics = fetchedTopics.sort((a, b) =>
        a.name.localeCompare(b.name)
      );

      setAllTopics(sortedTopics); // Store all topics

      if (searchKey) {
        const editTopic = sortedTopics.find((topic) => topic.key === searchKey);
        if (editTopic) {
          setSelectedTopic(editTopic);
          if (searchParams.has("goback")) {
            const goback = searchParams.get("goback");
            setGoBack(goback);
            setSearchParams((prevParams) => {
              const newParams = new URLSearchParams(prevParams);
              newParams.delete("goback");
              return newParams;
            });
          }
        } else {
          setSelectedTopic(null);
          setSearchParams((prevParams) => {
            const newParams = new URLSearchParams(prevParams);
            newParams.delete("key");
            return newParams;
          });
        }
      } else {
        setSelectedTopic(null);
      }
    } catch (error) {
      console.error("Error fetching topics:", error);
      showToast("Failed to fetch topics", "error");
    }
  }, [searchParams, setSearchParams, showToast]);

  useEffect(() => {
    fetchTags();
    fetchTopics();
  }, [fetchTopics]);

  // Initialize selected tags from URL params - used for bookmarking and sharing links
  const getInitialSelectedTags = useCallback(() => {
    const tagNamesFromUrl = searchParams.getAll("tag");
    if (tagNamesFromUrl.length === 0) return [];

    // Filter existing tags by name
    return tags.filter((tag) => tagNamesFromUrl.includes(tag.name));
  }, [searchParams, tags]);

  // Sync selected tags with URL params when tags are loaded
  useEffect(() => {
    if (tags.length > 0) {
      const initialTags = getInitialSelectedTags();
      setSelectedTags(initialTags);
    }
  }, [tags, getInitialSelectedTags]);

  // Filter topics based on selected tags
  useEffect(() => {
    if (selectedTags.length === 0) {
      setTopics(allTopics);
    } else {
      const filtered = allTopics.filter((topic) =>
        selectedTags.some((selectedTag) =>
          topic.tags.some((topicTag) => topicTag.id === selectedTag.id)
        )
      );
      setTopics(filtered);
    }
  }, [allTopics, selectedTags]); // Depend on allTopics instead of topics

  useEffect(() => {
    window.addEventListener("topicCreated", fetchTopics);
    return () => {
      window.removeEventListener("topicCreated", fetchTopics);
    };
  }, [fetchTopics]);

  const handleClosePromptEditor = useCallback(async () => {
    if (goBack) {
      const goto = goBack;
      setGoBack(null);
      navigate(goto + "?reload=true");
    } else {
      setSearchParams({});
      setSelectedTopic(null);
    }
  }, [setSearchParams, goBack, navigate, fetchTopics]);

  const handleAddNewTopic = useCallback(async () => {
    const randomFourDigit = Math.floor(1000 + Math.random() * 9000);
    try {
      const topic = await createTopic({
        name: `New Topic ${randomFourDigit}`,
        key: uuidv4(),
        description: "",
      });
      const topicVersion = await createTopicVersion({
        topic_key: topic.key,
        prompt_id: null,
        version_comment: "Initial version",
        visibility: [],
        supported_modalities: ["voice", "text"],
        fragments: [],
        prompts: null,
      });
      await fetchTopics();
      setSelectedTopic(topic);
      setSearchParams((prevParams) => {
        const newParams = new URLSearchParams(prevParams);
        newParams.set("key", topic.key);
        return newParams;
      });
      showToast("New topic created successfully", "success");
    } catch (error) {
      console.error("Error creating new topic:", error);
      showToast("Failed to create new topic", "error");
    }
  }, [fetchTopics, setSearchParams, showToast]);

  const handleTopicClick = useCallback(
    (topic) => {
      setSelectedTopic(topic);
      setSearchParams((prevParams) => {
        const newParams = new URLSearchParams(prevParams);
        newParams.set("key", topic.key);
        return newParams;
      });
    },
    [setSearchParams]
  );

  const handleSearch = useCallback((term) => {
    // This function is left empty as search is handled internally by DesktopView
  }, []);

  const handleSaveTopic = useCallback((topicData) => {
    // Implement save logic if needed
  }, []);

  const handleDeleteTopic = useCallback((topicId) => {
    // Implement delete logic if needed
  }, []);

  const handleResetTopic = useCallback((topicId) => {
    // Implement reset logic if needed
  }, []);

  const handleRestoreDeleted = useCallback(async () => {
    const deleted = await getDeletedTopics();
    const sorted = deleted.sort((a, b) => {
      return new Date(b.updated_at) - new Date(a.updated_at);
    });
    openCardSelector({
      items: sorted,
      CardComponent: LazyTopicCard, // TopicCard,
      title: "Select a Deleted Topic to Restore",
      onSelect: (topic) => {
        const restored = JSON.parse(JSON.stringify(topic));
        restored.name = `${
          topic.name
        } (Restored ${new Date().toLocaleString()})`;
        restored.is_deleted = false;
        updateTopic(restored).then(() => {
          fetchTopics();
          showToast(
            `Topic restored successfully as ${restored.name}`,
            "success"
          );
        });
      },
    });
  }, [fetchTopics, openCardSelector, showToast]);

  const handleMigrateTopics = useCallback(async () => {
    try {
      await migratePrompts();
      showToast("Topics migrated successfully", "success");
      fetchTopics();
    } catch (error) {
      console.error("Error migrating topics:", error);
      showToast("Failed to migrate topics", "error");
    }
  }, [fetchTopics, showToast]);

  const handleMigrateTags = async () => {
    try {
      await tagService.migrateTagsFromPrompts(true);
      showToast("Tags migrated successfully", "success");
      fetchTags();
      fetchTopics();
    } catch (error) {
      console.error("Error migrating tags:", error);
      showToast("Failed to migrate tags", "error");
    }
  };

  const handleMigratePrompts = useCallback(async () => {
    try {
      await migratePromptData();
      showToast("Prompts migrated successfully", "success");
      // fetchTopics();
    } catch (error) {
      console.error("Error migrating prompts:", error);
      showToast("Failed to migrate prompts", "error");
    }
  }, [fetchTopics, showToast]);

  const handleTagsChange = useCallback(
    (newSelectedTags) => {
      setSelectedTags(newSelectedTags);

      // Update URL params
      setSearchParams((prevParams) => {
        const newParams = new URLSearchParams(prevParams);

        // Remove all existing tags
        newParams.delete("tag");

        // Add new tag names
        newSelectedTags.forEach((tag) => {
          newParams.append("tag", tag.name);
        });

        return newParams;
      });
    },
    [setSearchParams]
  );

  const onTagCreate = async (newTag) => {
    try {
      const createdTag = await tagService.createTag(newTag);
      if (createdTag) {
        await fetchTags();
      }
      showToast(`Tag with name ${newTag} created successfully`, "success");
    } catch (error) {
      console.error("Error creating tag:", error);
    }
  };

  const handleTopicImport = async (topicData, forced) => {
    try {
      topicData.forEach((importedTopic) => {
        importedTopic.prompts.forEach((prompt) => {
          prompt.content_hash = prompt.content_hash.toString();
        });
      });
      const importedTopics = await importTopics({
        topicVersionsImports: topicData,
        forced: forced,
      });
      if (!importedTopics || importedTopics.length === 0) {
        showToast("No topic data to import", "error");
        return;
      }

      showToast("Topics imported successfully", "success");
      await fetchTopics();
      setImporting(false);
    } catch (error) {
      console.error("Error importing topic:", error);
      showToast("Failed to import topic " + error.message, "error");
    }
  };

  const orderedTopics = useMemo(
    () => sortPinnedFirst(topics, (t) => t.key),
    [topics, sortPinnedFirst],
  );

  return (
    <>
      {!selectedTopic && (
        <div className="absolute top-5 left-1/4 -translate-x-1/4 flex space-x-4 z-[1]">
          {/* <ToggleControl
            offLabel="Showcase"
            onLabel="Developer"
            offIcon=""
            onIcon=""
            initialState={currentView === 'developer'}
            onChange={(on) => {
              setCurrentView(on ? "developer" : "showcase");
            }}
            useActiveForBothStates={true}
          /> */}
          {currentView === "developer" && (
            <ToggleControl
              offLabel="Detailed"
              onLabel="Compact"
              offIcon=""
              onIcon=""
              initialState={compactView}
              onChange={(on) => {
                setCompactView(on);
              }}
              useActiveForBothStates={true}
            />
          )}
          {hasMigrateTopicsEnabled && (
            <button
              onClick={handleMigrateTopics}
              className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded shadow"
            >
              Migrate Topics
            </button>
          )}
          {hasMigratePromptsEnabled && (
            <button
              onClick={handleMigratePrompts}
              className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded shadow"
            >
              Migrate Prompts
            </button>
          )}
          <div className="ml-4">
            <button
              onClick={() => setImporting(true)}
              className="text-gray-400 hover:text-gray-300 p-2 rounded-md flex items-center space-x-1"
            >
              <FaDownload size={15} /> Import Topics
            </button>
          </div>
        </div>
      )}
      {/* Migrate Tags button. TODO: delete this once tag migrations are run in all envs */}
      {hasMigrateTagsEnabled && (
        <button
          onClick={handleMigrateTags}
          className="absolute top-4 right-0 mr-60 bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded shadow"
        >
          Migrate Tags
        </button>
      )}
      {!selectedTopic && !hasMigrateTagsEnabled && (
        <div className="absolute top-4 right-0 pr-60">
          <TagDropdown
            tags={tags}
            selectedTags={selectedTags}
            onTagsChange={handleTagsChange}
            onTagCreate={onTagCreate}
            placeholder="Filter by tags..."
          />
        </div>
      )}
      {importing && (
        <ImportModal
          onClose={() => setImporting(false)}
          onImport={handleTopicImport}
          importType={"topic"}
          multiple={true}
        />
      )}
      <DesktopView
        items={orderedTopics}
        selectedItem={selectedTopic}
        onSearch={handleSearch}
        searchPlaceholder="Filter topics ..."
        contextMenuItems={[
          {
            label: "Restore Deleted",
            onClick: () => {
              handleRestoreDeleted();
            },
          },
        ]}
        onItemClick={handleTopicClick}
        onSave={handleSaveTopic}
        onDelete={handleDeleteTopic}
        onReset={handleResetTopic}
        onClose={handleClosePromptEditor}
        Card={
          currentView !== "developer"
            ? ShowcaseCard
            : compactView
            ? LazyCompactTopicCard
            : LazyTopicCard
        }
        Editor={PromptEditor}
        addNewItem={handleAddNewTopic}
        eventName="topicCreated"
        hideOnSelect={true}
        fallback={
          <div>
            <LoadingTopicCard />
          </div>
        }
      />
    </>
  );
};

export default TopicDesktop;
